// src/Pages/Studio/UserBilling.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Link, useParams, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, setDoc, increment, serverTimestamp, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../../../firebase';
import { useAuth } from '../../../context/AuthContext';
import { useContactModal } from '../../../context/modalDisclosureContexts';
import {
  FREE_PLAN_CAPTURE_LIMIT,
  PROFESSIONAL_MONTHLY_CAPTURE_LIMIT,
  STUDIO_MONTHLY_CAPTURE_LIMIT,
} from '../../../constants/uploadLimits';
import { useStudioTopBarPlanPill } from '../../../hooks/useStudioTopBarPlanPill';
import { CURRENCIES, formatPrice } from '../../../utils/currency';
import toast from 'react-hot-toast';
import firebaseErrorMessage from '../../../utils/firebaseErrorMessage';
import { studioMyStudioPath } from '../../../constants/studioProjects';
import {
  getFreePlanFeatures,
  getProfessionalPlanFeatures,
  getStudioPlanFeatures,
  getEnterprisePlanFeatures,
  getPlanSubtitle,
} from '../../../constants/pricingPlanFeatures';
import { PlanFeatureList } from '../../../components/PlanFeatureList';
import { VolugraphMiniGaugeSpinner } from '../../../components/VolugraphMiniGaugeSpinner.js';
import {
  PROFESSIONAL_SUBSCRIPTION_USD_MONTHLY,
  PROFESSIONAL_SUBSCRIPTION_USD_YEARLY,
  STUDIO_SUBSCRIPTION_USD_MONTHLY,
  STUDIO_SUBSCRIPTION_USD_YEARLY,
  annualPlanSavingsPercent,
  roundedMonthlyEquivalentFromAnnualUsd,
} from '../../../constants/subscriptionListPricesUsd';
import { BillingPeriodToggle } from '../../../components/BillingPeriodToggle';
import SeatManagerSection from './SeatManagerSection.js';
import InvoicesSection from './InvoicesSection.js';
import { WORKSPACE_SEATS_ENABLED } from '../../../constants/seatPricing';
import './UserBilling.css';
import '../../Pricing/Pricing.css';

const BILLING_PLANS = [
  {
    id: 'free',
    title: 'Free',
    subtitle: getPlanSubtitle('free'),
    priceUsd: 0,
    priceSuffix: '',
    features: getFreePlanFeatures(),
  },
  {
    id: 'professional',
    title: 'Professional',
    subtitle: getPlanSubtitle('professional'),
    priceUsd: PROFESSIONAL_SUBSCRIPTION_USD_MONTHLY,
    priceSuffix: '/month',
    features: getProfessionalPlanFeatures(),
  },
  {
    id: 'studio',
    title: 'Studio',
    subtitle: getPlanSubtitle('studio'),
    priceUsd: STUDIO_SUBSCRIPTION_USD_MONTHLY,
    priceSuffix: '/month',
    recommended: true,
    features: getStudioPlanFeatures(),
  },
  {
    id: 'enterprise',
    title: 'Enterprise',
    subtitle: getPlanSubtitle('enterprise'),
    priceUsd: 0,
    priceSuffix: '',
    isEnterprise: true,
    features: getEnterprisePlanFeatures(),
  },
];

const UserBilling = () => {
  const { studioID } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const checkoutToastDoneRef = useRef(false);
  const { user } = useAuth();
  const { openModal: openContactModal, openContactSalesModal } = useContactModal();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [checkoutLoadingPlan, setCheckoutLoadingPlan] = useState(null);
  const [syncBillingLoading, setSyncBillingLoading] = useState(false);
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [currencyDropdownOpen, setCurrencyDropdownOpen] = useState(false);
  const [billingInterval, setBillingInterval] = useState('month');
  const currencyDropdownRef = useRef(null);
  const currency = CURRENCIES.find((c) => c.code === currencyCode) || CURRENCIES[0];
  const professionalAnnualSavingsPct = annualPlanSavingsPercent(
    PROFESSIONAL_SUBSCRIPTION_USD_MONTHLY,
    PROFESSIONAL_SUBSCRIPTION_USD_YEARLY
  );
  const studioAnnualSavingsPct = annualPlanSavingsPercent(
    STUDIO_SUBSCRIPTION_USD_MONTHLY,
    STUDIO_SUBSCRIPTION_USD_YEARLY
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (currencyDropdownRef.current && !currencyDropdownRef.current.contains(e.target)) {
        setCurrencyDropdownOpen(false);
      }
    };
    if (currencyDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [currencyDropdownOpen]);
  const routingUid = user?.uid || studioID;
  /** Each user has their own billing — no workspace routing indirection. */
  const payerBillingUid = user?.uid;
  const {
    planKey: currentPlan,
    lifetimeCaptureCount,
    proPeriodCaptureCount,
  } = useStudioTopBarPlanPill(user);
  const billingMetricsRef = user?.uid ? doc(db, 'users', user.uid, 'billing', 'metrics') : null;
  const [stripeCustomerId, setStripeCustomerId] = useState(null);
  const hasStripeCustomer = Boolean(stripeCustomerId);

  useEffect(() => {
    if (checkoutToastDoneRef.current) return;
    const checkout = searchParams.get('checkout');
    if (!checkout) return;
    checkoutToastDoneRef.current = true;
    if (checkout === 'cancel') {
      toast('Checkout cancelled — no changes were made.');
    } else if (checkout === 'success') {
      toast.success('If your plan did not update, use Sync subscription below.');
    }
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!user?.uid) {
      setStripeCustomerId(null);
      return undefined;
    }
    const planRef = doc(db, 'users', payerBillingUid, 'billing', 'plan');
    const unsub = onSnapshot(
      planRef,
      (snap) => {
        const id = snap.exists() ? snap.data()?.payment?.stripeCustomerId : null;
        setStripeCustomerId(typeof id === 'string' && id.trim() ? id.trim() : null);
      },
      () => setStripeCustomerId(null)
    );
    return () => unsub();
  }, [user?.uid, payerBillingUid]);

  const seatsPlanBanner = useMemo(() => {
    if (currentPlan === 'enterprise') {
      return {
        title: 'Enterprise plan',
        text: 'Enterprise — fair use applies (see Learn). Keep creating.',
      };
    }
    if (currentPlan === 'professional') {
      return {
        title: 'Professional plan',
        text:
          proPeriodCaptureCount != null
            ? `${proPeriodCaptureCount}/${PROFESSIONAL_MONTHLY_CAPTURE_LIMIT} captures this period`
            : getPlanSubtitle('professional'),
      };
    }
    if (currentPlan === 'studio') {
      return {
        title: 'Studio plan',
        text:
          proPeriodCaptureCount != null
            ? `${proPeriodCaptureCount}/${STUDIO_MONTHLY_CAPTURE_LIMIT} captures this period`
            : getPlanSubtitle('studio'),
      };
    }
    return null;
  }, [currentPlan, proPeriodCaptureCount]);

  const ensureBillingMetricsDoc = useCallback(async () => {
    if (!billingMetricsRef) return;
    try {
      await setDoc(billingMetricsRef, {
        userId: user?.uid || null,
        cancelledPlanCount: increment(0),
        freeCount: increment(0),
        professionalCount: increment(0),
        studioCount: increment(0),
        enterpriseCount: increment(0),
        updatedAt: serverTimestamp(),
      }, { merge: true });
    } catch (_) {}
  }, [billingMetricsRef, user?.uid]);

  const trackPlanSelection = useCallback(async (planId) => {
    if (!billingMetricsRef || !planId) return;
    const metricFieldByPlan = {
      free: 'freeCount',
      professional: 'professionalCount',
      studio: 'studioCount',
      enterprise: 'enterpriseCount',
    };
    const metricField = metricFieldByPlan[planId];
    if (!metricField) return;
    await ensureBillingMetricsDoc();
    await setDoc(billingMetricsRef, {
      [metricField]: increment(1),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [billingMetricsRef, ensureBillingMetricsDoc]);

  const trackCancelClick = useCallback(async () => {
    if (!billingMetricsRef) return;
    await ensureBillingMetricsDoc();
    await setDoc(billingMetricsRef, {
      cancelledPlanCount: increment(1),
      updatedAt: serverTimestamp(),
    }, { merge: true });
  }, [billingMetricsRef, ensureBillingMetricsDoc]);

  const handleManagePayment = async () => {
    setPaymentLoading(true);
    try {
      const createPortal = httpsCallable(functions, 'createStripePortalSession');
      const { data } = await createPortal({ returnUrl: window.location.href });
      const url = data?.url;
      if (url && typeof url === 'string') {
        window.location.href = url;
      } else {
        toast.error('Could not open payment settings');
      }
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Failed to open payment settings'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      await trackCancelClick();
    } catch (_) {}
    await handleManagePayment();
  };

  const CHECKOUT_TIMEOUT_MS = 25000;

  const handlePlanCheckout = async (planId) => {
    if (!user?.uid || !planId) return;
    setCheckoutLoadingPlan(planId);
    try {
      await trackPlanSelection(planId).catch(() => {});
      const createCheckout = httpsCallable(functions, 'createStripeCheckoutSession');
      const origin = window.location.origin;
      const successUrl = `${origin}${studioMyStudioPath(routingUid)}?checkout=success&plan=${encodeURIComponent(planId)}`;
      const cancelUrl = `${origin}/studio/${routingUid}/billing?checkout=cancel&plan=${encodeURIComponent(planId)}`;
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Request timed out. Please try again.')), CHECKOUT_TIMEOUT_MS);
      });
      const { data } = await Promise.race([
        createCheckout({ planId, successUrl, cancelUrl, billingInterval, extraSeats: 0 }),
        timeoutPromise,
      ]);
      const url = data?.url;
      if (url && typeof url === 'string') {
        window.location.href = url;
      } else {
        toast.error('Could not start checkout');
      }
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Failed to start checkout'));
    } finally {
      setCheckoutLoadingPlan(null);
    }
  };

  const handleSyncBillingFromStripe = async () => {
    if (!user?.uid || syncBillingLoading) return;
    setSyncBillingLoading(true);
    try {
      const sync = httpsCallable(functions, 'syncBillingFromStripe');
      const { data } = await sync();
      window.dispatchEvent(new Event('starling:refresh-user-plan'));
      if (data?.synced) {
        toast.success(`Subscription updated (${data.plan})`);
      } else if (data?.reason === 'no_active_subscription') {
        toast('No active subscription found in Stripe for this account.');
      } else {
        toast('Could not find a paid subscription to sync.');
      }
    } catch (err) {
      toast.error(firebaseErrorMessage(err, 'Could not sync with Stripe'));
    } finally {
      setSyncBillingLoading(false);
    }
  };

  const billingContent = (
    <div className="studio-billing">
      <aside className="studio-settings-mobile-sidebar">
        <div className="studio-section">
          <div className="studio-section-title">Settings</div>
          <NavLink to={`/studio/${studioID}/settings`} className={({ isActive }) => `studio-nav-item${isActive ? ' is-active' : ''}`}>Settings</NavLink>
        </div>
        <div className="studio-section">
          <div className="studio-section-title">Account</div>
          <NavLink to={`/studio/${studioID}/billing`} className={({ isActive }) => `studio-nav-item${isActive ? ' is-active' : ''}`}>Billing</NavLink>
          <Link to="/pricing" className="studio-nav-item">Pricing</Link>
        </div>
        <div className="studio-section">
          <div className="studio-section-title">Support</div>
          <Link to="/documentation" className="studio-nav-item">Help</Link>
          <button type="button" className="studio-nav-item" onClick={openContactModal}>Contact Us</button>
        </div>
      </aside>

      <div className="studio-settings-content">
        <header className="studio-page-heading">
          <h1 className="studio-page-title">Billing and Usage</h1>
        </header>
        <div className="pricing-toolbar-block studio-billing-pricing-toolbar">
          <div className="pricing-toolbar">
            <div className="pricing-toolbar-currency">
              <div className="studio-billing-currency-select-wrapper" ref={currencyDropdownRef}>
                <button
                  type="button"
                  id="billing-currency"
                  className="studio-billing-currency-select"
                  onClick={() => setCurrencyDropdownOpen((open) => !open)}
                  aria-haspopup="listbox"
                  aria-expanded={currencyDropdownOpen}
                  aria-label="Select display currency"
                >
                  <img src={currency.flagImg} alt="" className="studio-billing-currency-flag-img" width={24} height={24} />
                  <span className="studio-billing-currency-label-inline">{currency.label} ({currency.code})</span>
                  <span className="studio-billing-currency-select-chevron" aria-hidden="true" />
                </button>
                {currencyDropdownOpen && (
                  <ul
                    className="studio-billing-currency-list"
                    role="listbox"
                    aria-label="Currency options"
                  >
                    {CURRENCIES.map((c) => (
                      <li
                        key={c.code}
                        role="option"
                        aria-selected={c.code === currencyCode}
                        className={`studio-billing-currency-option${c.code === currencyCode ? ' is-selected' : ''}`}
                        onClick={() => {
                          setCurrencyCode(c.code);
                          setCurrencyDropdownOpen(false);
                        }}
                      >
                        <img src={c.flagImg} alt="" className="studio-billing-currency-flag-img" width={24} height={24} />
                        <span>{c.label} ({c.code})</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
            <div className="pricing-toolbar-toggle">
              <BillingPeriodToggle
                value={billingInterval}
                onChange={setBillingInterval}
                className="studio-billing-period-toggle"
              />
            </div>
            <div className="pricing-toolbar-spacer" aria-hidden="true" />
          </div>
          <p className="studio-billing-currency-note">
            Pricing is in USD. Other currencies are approximate.
          </p>
        </div>
        <div className="studio-page-content studio-billing-plans-wrap">
          <div className="plans-container studio-billing-plans">
            {BILLING_PLANS.map((plan) => {
              const isCurrentPlan = currentPlan === plan.id;
              return (
                <div key={plan.id} className={`plan-box ${plan.recommended ? 'plan-recommended' : ''} ${plan.id === 'enterprise' ? 'plan-enterprise' : ''} ${plan.id === 'free' ? 'plan-box-free' : ''} ${isCurrentPlan ? 'plan-box-current' : ''}`}>
                  {plan.recommended && <div className="plan-recommended-strip">Recommended</div>}
                  <div className="plan-header">
                    <h2 className="plan-title">{plan.title}</h2>
                    <div className="plan-subtitle">{plan.subtitle}</div>
                    <div
                      className={`plan-price${
                        (plan.id === 'professional' || plan.id === 'studio') &&
                        billingInterval === 'year'
                          ? ' plan-price--yearly-inline'
                          : ''
                      }`}
                    >
                      {plan.isEnterprise ? (
                        'Custom'
                      ) : plan.id === 'professional' && billingInterval === 'year' ? (
                        <>
                          <span className="plan-price-annual-figures">
                            {formatPrice(
                              roundedMonthlyEquivalentFromAnnualUsd(PROFESSIONAL_SUBSCRIPTION_USD_YEARLY),
                              currency
                            )}
                            {plan.priceSuffix}
                          </span>
                          <span className="plan-price-annual-addon">
                            {professionalAnnualSavingsPct > 0 && (
                              <span className="plan-price-savings-badge">
                                {professionalAnnualSavingsPct}% OFF
                              </span>
                            )}
                            <span
                              className="plan-price-monthly-strikethrough"
                              title="Standard monthly list price"
                            >
                              {formatPrice(PROFESSIONAL_SUBSCRIPTION_USD_MONTHLY, currency)}
                            </span>
                          </span>
                        </>
                      ) : plan.id === 'studio' && billingInterval === 'year' ? (
                        <>
                          <span className="plan-price-annual-figures">
                            {formatPrice(
                              roundedMonthlyEquivalentFromAnnualUsd(STUDIO_SUBSCRIPTION_USD_YEARLY),
                              currency
                            )}
                            {plan.priceSuffix}
                          </span>
                          <span className="plan-price-annual-addon">
                            {studioAnnualSavingsPct > 0 && (
                              <span className="plan-price-savings-badge">
                                {studioAnnualSavingsPct}% OFF
                              </span>
                            )}
                            <span
                              className="plan-price-monthly-strikethrough"
                              title="Standard monthly list price"
                            >
                              {formatPrice(STUDIO_SUBSCRIPTION_USD_MONTHLY, currency)}
                            </span>
                          </span>
                        </>
                      ) : (
                        `${formatPrice(plan.priceUsd, currency)}${plan.priceSuffix}`
                      )}
                    </div>
                  </div>
                  <div className="plan-divider" />
                  <div className="plan-section">
                    <PlanFeatureList
                      features={plan.features}
                      formatLine={(text) =>
                        typeof text === 'string' && text.includes('__EXTRA_CAPTURE_PRICE__')
                          ? text.replace(
                              '__EXTRA_CAPTURE_PRICE__',
                              formatPrice(plan.extraCapturePriceUsd ?? 0, currency)
                            )
                          : text
                      }
                    />
                  </div>
                  {isCurrentPlan ? (
                    <button type="button" className="plan-cta plan-cta-metal plan-cta-current" disabled>
                      Current Plan
                    </button>
                  ) : plan.id === 'free' ? (
                    <span className="plan-cta plan-cta-metal">Free Tier</span>
                  ) : plan.isEnterprise ? (
                    <button type="button" className="plan-cta plan-cta-metal" onClick={() => openContactSalesModal()}>
                      Contact Sales
                    </button>
                  ) : (
                    <button
                      type="button"
                      className={`plan-cta plan-cta-metal${checkoutLoadingPlan === plan.id ? ' plan-cta--loading' : ''}`}
                      onClick={() => handlePlanCheckout(plan.id)}
                      disabled={!!checkoutLoadingPlan}
                    >
                      {checkoutLoadingPlan === plan.id ? (
                        <>
                          <VolugraphMiniGaugeSpinner className="plan-cta-processing-icon" />
                          Processing…
                        </>
                      ) : (
                        'Choose Plan'
                      )}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {WORKSPACE_SEATS_ENABLED && payerBillingUid ? (
            <div className="studio-page-content studio-billing-plans-wrap studio-billing-seats-wrap">
              {seatsPlanBanner ? (
                <section className="studio-billing-plan-banner" aria-label={seatsPlanBanner.title}>
                  <h2 className="studio-billing-plan-banner-title">{seatsPlanBanner.title}</h2>
                  <p className="studio-billing-plan-banner-text">{seatsPlanBanner.text}</p>
                </section>
              ) : null}
              <SeatManagerSection
                ownerUid={payerBillingUid}
                currentUserUid={user.uid}
                ownerEmail={user.email}
                billingPlanKey={currentPlan}
                currentAddonQty={user.workspaceSeatAddonQuantity ?? 0}
                seatCap={user.workspaceSeatCap ?? null}
              />
            </div>
          ) : null}

        {(currentPlan === 'free' && lifetimeCaptureCount != null) && (
          <section className="studio-billing-free-usage studio-billing-free-usage--viewer">
            <div className="studio-billing-free-usage-text-wrap">
              <h2 className="studio-billing-free-usage-title">Free Plan Usage</h2>
              <p className="studio-billing-free-usage-text">
                {Math.max(0, FREE_PLAN_CAPTURE_LIMIT - Math.min(lifetimeCaptureCount, FREE_PLAN_CAPTURE_LIMIT))}/{FREE_PLAN_CAPTURE_LIMIT} captures left
              </p>
            </div>
            <div className="studio-billing-usage-aside studio-billing-usage-aside--solo">
              {hasStripeCustomer ? (
                <button
                  type="button"
                  className="studio-billing-stripe-sync"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-icon" aria-hidden="true">
                    <i className={`ph ph-arrows-clockwise${syncBillingLoading ? ' studio-billing-stripe-sync-spin' : ''}`} />
                  </span>
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing with Stripe…' : 'Sync subscription'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">Paid but still see Free? Tap to refresh.</span>
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="studio-billing-stripe-sync studio-billing-stripe-sync--text"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing…' : 'Already paid? Refresh your subscription'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">No Stripe customer on file yet — still run a refresh if you finished checkout.</span>
                  </span>
                </button>
              )}
            </div>
          </section>
        )}

        {(currentPlan === 'professional' && proPeriodCaptureCount != null) && (
          <section className="studio-billing-free-usage studio-billing-pro-usage studio-billing-free-usage--viewer">
            <div className="studio-billing-free-usage-text-wrap">
              <h2 className="studio-billing-free-usage-title">Professional Plan Usage</h2>
              <p className="studio-billing-free-usage-text">
                {proPeriodCaptureCount}/{PROFESSIONAL_MONTHLY_CAPTURE_LIMIT} captures this period
              </p>
            </div>
            <div className="studio-billing-usage-aside studio-billing-usage-aside--solo">
              {hasStripeCustomer ? (
                <button
                  type="button"
                  className="studio-billing-stripe-sync"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-icon" aria-hidden="true">
                    <i className={`ph ph-arrows-clockwise${syncBillingLoading ? ' studio-billing-stripe-sync-spin' : ''}`} />
                  </span>
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing…' : 'Sync with Stripe'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">Refresh plan from your subscription</span>
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="studio-billing-stripe-sync studio-billing-stripe-sync--text"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing…' : 'Refresh subscription from Stripe'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">Use if checkout completed but billing shows no customer yet.</span>
                  </span>
                </button>
              )}
            </div>
          </section>
        )}

        {(currentPlan === 'studio' && proPeriodCaptureCount != null) && (
          <section className="studio-billing-free-usage studio-billing-pro-usage studio-billing-free-usage--viewer">
            <div className="studio-billing-free-usage-text-wrap">
              <h2 className="studio-billing-free-usage-title">Studio Plan Usage</h2>
              <p className="studio-billing-free-usage-text">
                {proPeriodCaptureCount}/{STUDIO_MONTHLY_CAPTURE_LIMIT} captures this period
              </p>
            </div>
            <div className="studio-billing-usage-aside studio-billing-usage-aside--solo">
              {hasStripeCustomer ? (
                <button
                  type="button"
                  className="studio-billing-stripe-sync"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-icon" aria-hidden="true">
                    <i className={`ph ph-arrows-clockwise${syncBillingLoading ? ' studio-billing-stripe-sync-spin' : ''}`} />
                  </span>
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing…' : 'Sync with Stripe'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">Refresh plan from your subscription</span>
                  </span>
                </button>
              ) : (
                <button
                  type="button"
                  className="studio-billing-stripe-sync studio-billing-stripe-sync--text"
                  onClick={handleSyncBillingFromStripe}
                  disabled={syncBillingLoading}
                >
                  <span className="studio-billing-stripe-sync-body">
                    <span className="studio-billing-stripe-sync-title">
                      {syncBillingLoading ? 'Syncing…' : 'Refresh subscription from Stripe'}
                    </span>
                    <span className="studio-billing-stripe-sync-sub">Use if checkout completed but billing shows no customer yet.</span>
                  </span>
                </button>
              )}
            </div>
          </section>
        )}

        <section className="studio-billing-payment">
          <div className="studio-billing-payment-text-wrap">
            <h2 className="studio-billing-payment-title">Payment</h2>
            <p className="studio-billing-payment-text">
              {hasStripeCustomer ?
                'Update your payment method, billing address, or view invoices in Stripe.' :
                'Subscribe to a paid plan to add a payment method. Invoices and cards are managed in Stripe after checkout.'}
            </p>
          </div>
          {hasStripeCustomer ? (
            <button
              type="button"
              className="studio-billing-payment-btn"
              onClick={handleManagePayment}
              disabled={paymentLoading}
            >
              {paymentLoading ? 'Processing…' : 'Manage Payment Details'}
            </button>
          ) : null}
        </section>

        <InvoicesSection hasStripeCustomer={hasStripeCustomer} />

        {currentPlan !== 'free' && hasStripeCustomer && (
          <section className="studio-billing-cancel">
            <div className="studio-billing-cancel-text-wrap">
              <h2 className="studio-billing-cancel-title">Cancel Plan</h2>
              <p className="studio-billing-cancel-warning">
                You&apos;ll switch to the Free plan. Fewer than {FREE_PLAN_CAPTURE_LIMIT} completed captures: keep capturing for free until you reach {FREE_PLAN_CAPTURE_LIMIT}. At {FREE_PLAN_CAPTURE_LIMIT} or more: your data stays, but you need an active paid plan to capture again.
              </p>
            </div>
            <button
              type="button"
              className="studio-billing-cancel-btn"
              onClick={handleCancelSubscription}
              disabled={paymentLoading}
            >
              {paymentLoading ? 'Processing…' : 'Cancel Plan'}
            </button>
          </section>
        )}

      </div>
    </div>
  );

  return billingContent;
};

export default UserBilling;
